<script setup>
/**
 * DigestMock — a Heimdall activity digest, redrawn as a UI card.
 * Mirrors the real digest structure: executive summary, temperature,
 * benchmarked KPIs, recommendations, confidence — language + PDF.
 * Decorative (aria-hidden); the informative alt lives on the section.
 */
</script>

<template>
  <div class="rounded-2xl border border-slate-200 bg-white shadow-[0_24px_60px_-24px_rgba(15,23,42,0.25)] overflow-hidden" aria-hidden="true">
    <!-- header -->
    <div class="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
      <div class="flex items-center gap-2.5">
        <svg viewBox="0 0 40 24" fill="none" class="w-8 h-5">
          <path d="M3 12 Q20 1 37 12 Q20 23 3 12 Z" stroke="#7c5ce0" stroke-width="1.6" />
          <circle cx="20" cy="12" r="5.5" stroke="#b04fd6" stroke-width="1.2" />
          <circle cx="20" cy="12" r="1.8" fill="#e8a34d" />
        </svg>
        <div>
          <div class="text-[13px] font-semibold text-slate-900 leading-tight">Heimdall — Digest d'activité</div>
          <div class="text-[11px] text-slate-500">Sondage satisfaction · Jour 2</div>
        </div>
      </div>
      <div class="flex items-center gap-1.5">
        <span class="text-[10px] font-mono border border-slate-200 rounded-md px-1.5 py-0.5 text-slate-500">FR</span>
        <span class="text-[10px] font-mono bg-slate-900 text-white rounded-md px-2 py-0.5">PDF</span>
      </div>
    </div>

    <div class="px-5 py-4 space-y-4">
      <!-- temperature -->
      <div>
        <div class="flex items-baseline justify-between mb-1.5">
          <span class="text-[11px] font-mono uppercase tracking-wider text-slate-400">Température</span>
          <span class="text-sm font-semibold text-slate-900 tabular-nums" dir="ltr">78 / 100</span>
        </div>
        <div class="h-1.5 rounded-full bg-slate-100 overflow-hidden" dir="ltr">
          <div class="h-full w-[78%] rounded-full bg-gradient-to-r from-primary-500 to-emerald-500"></div>
        </div>
      </div>

      <!-- KPIs vs benchmarks -->
      <div class="grid grid-cols-3 gap-2" dir="ltr">
        <div class="rounded-lg border border-slate-100 bg-slate-50 px-2.5 py-2">
          <div class="text-[10px] text-slate-500 mb-0.5">NPS</div>
          <div class="text-sm font-semibold text-slate-900 tabular-nums">+42</div>
          <div class="text-[10px] text-emerald-600 tabular-nums">réf. +31</div>
        </div>
        <div class="rounded-lg border border-slate-100 bg-slate-50 px-2.5 py-2">
          <div class="text-[10px] text-slate-500 mb-0.5">CSAT</div>
          <div class="text-sm font-semibold text-slate-900 tabular-nums">4,3/5</div>
          <div class="text-[10px] text-emerald-600 tabular-nums">réf. 4,0</div>
        </div>
        <div class="rounded-lg border border-slate-100 bg-slate-50 px-2.5 py-2">
          <div class="text-[10px] text-slate-500 mb-0.5">Réponses</div>
          <div class="text-sm font-semibold text-slate-900 tabular-nums">68 %</div>
          <div class="text-[10px] text-slate-400 tabular-nums">412 / 605</div>
        </div>
      </div>

      <!-- executive summary -->
      <div>
        <div class="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-1.5">Résumé exécutif</div>
        <p class="text-[12.5px] leading-relaxed text-slate-600">
          Satisfaction nettement au-dessus du référentiel. Le format table ronde
          tire les scores vers le haut ; la logistique déjeuner concentre
          l'essentiel des retours négatifs.
        </p>
      </div>

      <!-- recommendations -->
      <div>
        <div class="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-1.5">Recommandations</div>
        <ul class="space-y-1.5">
          <li class="flex gap-2 text-[12.5px] text-slate-600">
            <span class="text-primary-600 mt-px">→</span>
            Reconduire le format table ronde en ouverture du Jour 3.
          </li>
          <li class="flex gap-2 text-[12.5px] text-slate-600">
            <span class="text-primary-600 mt-px">→</span>
            Doubler les points de restauration entre 12 h et 13 h.
          </li>
        </ul>
      </div>
    </div>

    <!-- footer -->
    <div class="flex items-center justify-between px-5 py-2.5 bg-slate-50 border-t border-slate-100 text-[11px] text-slate-500">
      <span>Confiance : <span class="text-emerald-600 font-medium">élevée</span></span>
      <span class="tabular-nums" dir="ltr">412 réponses analysées</span>
    </div>
  </div>
</template>
